import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PORTFOLIO_PROJECTS } from '../data/portfolioData';
import { ProjectCard } from '../components/portfolio/ProjectCard';
import { ProjectModal } from '../components/portfolio/ProjectModal';

const HEIGHTS = ['aspect-[4/3]', 'aspect-[3/2]', 'aspect-[5/4]', 'aspect-square', 'aspect-[4/3]', 'aspect-[3/2]'];

const STATS = [
  { end: 13, suffix: '+', label: 'Projects Delivered', sub: 'Across MH & GJ' },
  { end: 100, suffix: 'k+', label: 'Sq. Ft. Built', sub: 'Industrial grade' },
  { end: 4, suffix: '+', label: 'Zones Covered', sub: 'MIDC | GIDC | Daund' },
];

const CountUp = ({ end, suffix, active }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let cur = 0;
    const inc = Math.max(1, Math.ceil(end / 50));
    const t = setInterval(() => {
      cur = Math.min(cur + inc, end);
      setVal(cur);
      if (cur >= end) clearInterval(t);
    }, 20);
    return () => clearInterval(t);
  }, [active, end]);
  return <>{val}{suffix}</>;
};

const PortfolioPage = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [statsActive, setStatsActive] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsActive(true); }, { threshold: 0.3 });
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="portfolio-page">
      {/* Page Header */}
      <div className="border-b border-[#4A4A4A] bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#6B7280] text-xs font-body uppercase tracking-widest hover:text-[#C41E3A] transition-colors mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft size={14} strokeWidth={1.5} /> Back to Home
          </Link>
          <p className="section-label mb-4">Our Work</p>
          <h1 className="font-heading text-5xl lg:text-7xl text-white">ALL PROJECTS</h1>
          <p className="text-[#9CA3AF] text-sm font-body mt-3 max-w-md">
            Every industrial construction project completed by Kadam Patil Projects — across Maharashtra and Gujarat.
          </p>
        </div>
      </div>

      {/* Stats Bar */}
      <div ref={statsRef} className="border-b border-[#4A4A4A] bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="flex flex-wrap gap-10 lg:gap-16">
            {STATS.map(({ end, suffix, label, sub }) => (
              <div key={label} className="border-l-2 border-[#C41E3A] pl-5">
                <div className="font-heading text-3xl lg:text-4xl text-white tabular-nums">
                  <CountUp end={end} suffix={suffix} active={statsActive} />
                </div>
                <div className="text-white text-xs uppercase tracking-widest font-body mt-1">{label}</div>
                <div className="text-[#6B7280] text-xs font-body mt-0.5">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Full Masonry Grid — all 13 projects */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-px" data-testid="all-projects-grid">
          {PORTFOLIO_PROJECTS.map((project, i) => (
            <div key={project.id} className="break-inside-avoid mb-px">
              <ProjectCard
                project={project}
                onClick={setSelectedProject}
                heightClass={HEIGHTS[i % HEIGHTS.length]}
              />
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 border border-[#4A4A4A] bg-[#0D0D0D] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="portfolio-page-cta">
          <div className="w-1 h-10 bg-[#C41E3A] shrink-0 hidden sm:block" />
          <div className="flex-1">
            <p className="font-heading text-xl text-white">PLANNING A NEW INDUSTRIAL FACILITY?</p>
            <p className="text-[#9CA3AF] text-sm font-body mt-0.5">
              From PEB sheds to large manufacturing complexes — Kadam Patil Projects delivers on time.
            </p>
          </div>
          <a
            href="/#contact"
            className="shrink-0 flex items-center gap-2 bg-[#C41E3A] text-white text-xs font-bold uppercase tracking-[0.2em] px-7 py-4 clip-btn hover:bg-white hover:text-[#0A0A0A] transition-colors whitespace-nowrap"
            data-testid="portfolio-page-cta-btn"
          >
            Get In Touch
          </a>
        </div>
      </div>

      {selectedProject && (
        <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
      )}
    </div>
  );
};

export default PortfolioPage;
